import { useState } from 'react';
import { Box, Card, CardContent, Typography, Stack, Chip, Button, LinearProgress, Divider } from '@mui/material';
import { Payments, Bolt, Description, EvStation, Download, ErrorOutline } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { AmFilterBar, type AmFilters } from '../../components/AmFilterBar';
import { amStationOpsApi } from '../../api/amStationOps.api';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

function Tile({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string; color?: string }) {
  return (
    <Card sx={{ flex: '1 1 180px', minWidth: 180 }}>
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: color ?? 'primary.main', color: '#fff' }}>{icon}</Box>
          <Box>
            <Typography variant="h6" fontWeight={800} lineHeight={1.1}>{value}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
          </Box>
        </Stack>
        {sub && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.75 }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export function AmDashboardPage() {
  const { hasPermission } = useAuth();
  const [filters, setFilters] = useState<AmFilters>({});
  const dashParams = { clientId: filters.clientId || undefined, city: filters.city || undefined, propertyType: filters.propertyType || undefined, ownerType: filters.ownerType || undefined, connectionType: filters.connectionType || undefined, from: filters.from || undefined, to: filters.to || undefined };
  const { data, isLoading } = useQuery({ queryKey: ['am-dashboard', filters], queryFn: () => amStationOpsApi.dashboard(dashParams) });
  const { data: monthly } = useQuery({ queryKey: ['am-monthly', filters], queryFn: () => amStationOpsApi.monthly(6, dashParams) });
  const maxCollected = Math.max(1, ...(monthly ?? []).map((m) => m.collected));

  return (
    <>
      <PageHeader title="Asset Management — Dashboard" subtitle="EV charging station operations overview."
        action={
          <Stack direction="row" spacing={1}>
            {hasPermission('am_rent.export') && <Button size="small" startIcon={<Download />} variant="outlined" onClick={() => amStationOpsApi.exportCsv('rent')}>Rent CSV</Button>}
            {hasPermission('am_electricity.export') && <Button size="small" startIcon={<Download />} variant="outlined" onClick={() => amStationOpsApi.exportCsv('electricity')}>Electricity CSV</Button>}
          </Stack>
        } />
      <AmFilterBar value={filters} onChange={setFilters} show={['client', 'city', 'property', 'owner', 'connection', 'dates']} />
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {data && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Tile icon={<EvStation />} label="Active stations" value={data.stations.active} color="#455a64" />
            <Tile icon={<Payments />} label="Rent paid to owners (this month)" value={inr(data.rent.collectedThisMonth)} sub={`${data.rent.pending} pending · ${data.rent.overdue} overdue`} color="#2e7d32" />
            <Tile icon={<ErrorOutline />} label="Rent due to pay (overdue)" value={inr(data.rent.overdueAmount)} color="#c62828" />
            <Tile icon={<Bolt />} label="Electricity paid (this month)" value={inr(data.electricity.paidAmountThisMonth)} sub={`${data.electricity.pending} pending · ${data.electricity.overdue} overdue`} color="#ef6c00" />
            <Tile icon={<Description />} label="Active agreements" value={data.agreements.active} sub={`${data.agreements.expiringSoon} expiring ≤30d`} color="#1565c0" />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Urgent */}
            <Card sx={{ flex: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Needs attention</Typography>
                {data.urgent.length === 0 && <Typography variant="body2" color="text.secondary">Nothing urgent — all caught up. 🎉</Typography>}
                <Stack divider={<Divider />} spacing={1}>
                  {data.urgent.map((u) => (
                    <Stack key={`${u.type}-${u.id}`} direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={u.type} color={u.type === 'rent' ? 'success' : 'info'} variant="outlined" />
                        <Typography variant="body2">{u.station ?? u.ref ?? '—'}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {u.amount != null && <Typography variant="body2" fontWeight={600}>{inr(u.amount)}</Typography>}
                        <Typography variant="caption" color="text.secondary">{u.date ? new Date(u.date).toLocaleDateString('en-IN') : ''}</Typography>
                        <Chip size="small" label={u.status} color={u.status === 'overdue' ? 'error' : u.status === 'expiring' ? 'warning' : 'default'} />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Monthly rent transferred to owners */}
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Rent transferred {filters.from || filters.to ? `(${filters.from ?? '…'} → ${filters.to ?? '…'})` : '(6 months)'}</Typography>
                <Stack spacing={1}>
                  {(monthly ?? []).map((m) => (
                    <Box key={m.month}>
                      <Stack direction="row" justifyContent="space-between"><Typography variant="caption">{m.month}</Typography><Typography variant="caption" fontWeight={600}>{inr(m.collected)}</Typography></Stack>
                      <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'action.hover' }}><Box sx={{ height: 8, borderRadius: 1, bgcolor: 'success.main', width: `${(m.collected / maxCollected) * 100}%` }} /></Box>
                    </Box>
                  ))}
                  {(monthly ?? []).length === 0 && <Typography variant="body2" color="text.secondary">No rent data yet.</Typography>}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Stack>
      )}
    </>
  );
}
